import {
  Add,
  AssignmentTurnedInOutlined,
  BedOutlined,
  Close,
  FilterList,
  RestaurantMenuOutlined,
  VisibilityOutlined,
  AssistantPhoto,
  PhotoLibrary,
  EventNote,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  IconButton as MuiIconButton,
  TextField,
  Toolbar,
  Typography,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  MenuItem,
  FormControl,
  Select,
  OutlinedInput,
  alpha,
  useTheme,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import { ChildCare } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/fr";
import EventDialog from "components/EventDialog";
import NappyIcon from "components/utils/NappyIcon";
import ChildPickerOption, {
  childPickerMenuItemSx,
  childPickerMenuProps,
} from "components/utils/ChildPickerOption";
import { stringAvatar } from "utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  EventsState,
  fetchEvents,
  invalidateEvents,
} from "store/features/events";
import { getEvents, getEventTypes, getNurseryEvents } from "apis";
import Event from "components/Event";
import CommentDialog from "components/CommentDialog";
import FrameworkIndicatorDialog from "components/FrameworkIndicatorDialog";
import { useTranslation } from "react-i18next";
import { AuthState } from "store/features/auth";
import useChildren from "hooks/useChildren";
import Axios from "axios";
import { useSnackbar } from "notistack";
import { isStaffRole } from "utils/roles";

const postTypes = [
  { label: "observation", Icon: VisibilityOutlined, name: "Observation" },
  { label: "nappy", Icon: NappyIcon, name: "Nappy" },
  { label: "sleep", Icon: BedOutlined, name: "Sleep" },
  { label: "meal", Icon: RestaurantMenuOutlined, name: "Meal" },
  { label: "assessment", Icon: AssignmentTurnedInOutlined, name: "Assessment" },
  { label: "photos", Icon: AssistantPhoto, name: "Photos" },
  { label: "menu", Icon: RestaurantMenuOutlined, name: "Menu" },
];

// Assessment intentionally excluded — those are created via /child/:id/development,
// which scopes them to a (areaId, ageBandId) cell and snapshots indicators properly.
// The timeline picker can't supply that context, so a created Assessment ends up
// with null cell metadata and breaks the indicator drawer + cell chip.
const speedDialActions = [
  { name: "Nappy", icon: <NappyIcon />, labelKey: "posts.nappy" },
  { name: "Sleep", icon: <BedOutlined />, labelKey: "posts.sleep" },
  { name: "Meal", icon: <RestaurantMenuOutlined />, labelKey: "posts.meal" },
  { name: "Photos", icon: <PhotoLibrary />, labelKey: "posts.photos" },
  {
    name: "Observation",
    icon: <VisibilityOutlined />,
    labelKey: "posts.observation",
  },
];

const Timeline: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const authInfo = useSelector<RootState, AuthState>((store) => store.auth);

  const [openFilter, setOpenFilter] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [commentDialog, setCommentDialog] = useState("");
  const [frameIndicatorDialog, setFrameIndicatorDialog] = useState("");
  const [eventType, setEventType] = useState("");
  const [editEvent, setEditEvent] = useState<any>(null);
  const [deleteEvent, setDeleteEvent] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const openCommentDialog = useCallback((id: string) => {
    setCommentDialog(id);
  }, []);
  const openFrameIndicatorDialog = useCallback((id: string) => {
    setFrameIndicatorDialog(id);
  }, []);

  const [from, setFrom] = useState<Dayjs | null>(dayjs().subtract(30, "day"));
  const [to, setTo] = useState<Dayjs | null>(dayjs());
  const [typeSelected, setTypeSelected] = useState<Record<string, boolean>>({
    Snapshot: true,
    Observation: true,
    Nappy: true,
    Sleep: true,
    Meal: true,
    Assessment: true,
    Transactions: true,
    Photos: true,
    Menu: true,
  });
  const [childrenSelected, setChildrenSelected] = useState<string[]>([]);

  const { isLoaded, events } = useSelector<RootState, EventsState>(
    (store) => store.events,
  );
  const { loaded: childrenLoaded, children } = useChildren();
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(invalidateEvents());
    };
  }, [dispatch]);

  const previousLanguage = useRef(i18n.language);
  useEffect(() => {
    if (previousLanguage.current !== i18n.language) {
      previousLanguage.current = i18n.language;
      dispatch(invalidateEvents());
    }
  }, [dispatch, i18n.language]);

  const hasChildren = children.length > 0;
  useEffect(() => {
    const isParent = authInfo.role === "PARENT";
    if (!isLoaded && (!isParent || hasChildren)) {
      const eventsFn = isParent ? getEvents : getNurseryEvents;
      Promise.all([eventsFn(), getEventTypes()]).then(
        ([events, eventTypes]) => {
          dispatch(
            fetchEvents({ events: events.data, eventTypes: eventTypes.data }),
          );
        },
      );
    }
  }, [isLoaded, dispatch, hasChildren, authInfo.role]);

  const isParent = authInfo.role === "PARENT";

  const filteredEvents = useMemo(() => {
    const noChildFilter = childrenSelected.length === 0;
    const matchesChild = (child: any) =>
      noChildFilter ||
      childrenSelected.includes(`${child.firstName} ${child.lastName}`);

    if (!openFilter) {
      return events.filter((event) => matchesChild(event.child));
    }
    return events.filter((event) => {
      const createdAtDate = dayjs(event.createdAt);
      return (
        createdAtDate.isBefore(to) &&
        createdAtDate.isAfter(from) &&
        typeSelected[event.eventType.name] &&
        matchesChild(event.child)
      );
    });
  }, [from, to, typeSelected, events, openFilter, childrenSelected]);

  useEffect(() => {
    if (children[0]) {
      // Default child filter to "all" (empty selection) — users can narrow
      // down via the multi-select. Previously defaulted to the first child,
      // which silently hid siblings until the user noticed.
      const earliest = children.reduce(
        (acc, c) =>
          c.dateOfBirth && dayjs(c.dateOfBirth).isBefore(acc)
            ? dayjs(c.dateOfBirth)
            : acc,
        dayjs(children[0].dateOfBirth),
      );
      setFrom(earliest);
      setChildrenSelected([]);
    }
  }, [children]);

  const visibleTypeNames = useMemo(() => postTypes.map((p) => p.name), []);
  const activeFilterCount = useMemo(() => {
    return visibleTypeNames.filter((name) => !typeSelected[name]).length;
  }, [typeSelected, visibleTypeNames]);

  const postItems = useMemo(() => {
    return postTypes.map(({ label, Icon, name }) => {
      const active = typeSelected[name];
      return (
        <Chip
          key={label}
          icon={Icon && <Icon />}
          label={t(`posts.${label}`)}
          variant={active ? "filled" : "outlined"}
          color={active ? "primary" : "default"}
          onClick={() =>
            setTypeSelected((value) => ({ ...value, [name]: !value[name] }))
          }
          sx={{
            fontWeight: active ? 600 : 400,
            transition: "all 0.2s",
            borderRadius: 2,
            "&:hover": {
              boxShadow: active ? "none" : 1,
            },
          }}
        />
      );
    });
  }, [t, typeSelected]);

  const handleDelete = useCallback((item: any) => {
    setDeleteEvent(item);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteEvent) return;
    setDeleting(true);
    Axios.delete(`/api/events/${deleteEvent.id}`)
      .then(() => {
        enqueueSnackbar(t("timeline.event_deleted"), { variant: "success" });
        dispatch(invalidateEvents());
        setDeleteEvent(null);
      })
      .catch(() => {
        enqueueSnackbar(t("timeline.event_delete_failed"), {
          variant: "error",
        });
      })
      .finally(() => setDeleting(false));
  }, [deleteEvent, t, dispatch, enqueueSnackbar]);

  const handleEdit = useCallback((item: any) => {
    setEditEvent(item);
    setEventType(item.eventType.name);
    setOpenDialog(true);
  }, []);

  const handleAddEvent = useCallback((type: string) => {
    setEventType(type);
    setEditEvent(null);
    setOpenDialog(true);
  }, []);

  const isStaff =
    authInfo.role === "NURSERY_ADMIN" || isStaffRole(authInfo.role);
  const isLoading = !childrenLoaded || !isLoaded;

  return (
    <>
      {/* Filter bar */}
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
          minHeight={56}
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
            <ChildCare sx={{ color: "text.secondary", fontSize: 20 }} />
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 140, sm: 200 },
                maxWidth: { xs: "100%", sm: 400 },
              }}
            >
              <Select
                multiple
                displayEmpty
                value={childrenSelected}
                onChange={(e) =>
                  setChildrenSelected(
                    Array.isArray(e.target.value)
                      ? e.target.value
                      : [e.target.value],
                  )
                }
                input={<OutlinedInput />}
                MenuProps={childPickerMenuProps}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <Typography color="text.disabled" variant="body2">
                        {t("placeholders.select_child")}
                      </Typography>
                    );
                  }
                  return (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((displayName) => {
                        const c = children.find(
                          (ch) =>
                            `${ch.firstName} ${ch.lastName}` === displayName,
                        );
                        return (
                          <Chip
                            key={displayName}
                            label={displayName}
                            size="small"
                            avatar={
                              c?.photo ? (
                                <Avatar src={c.photo as string} />
                              ) : (
                                <Avatar
                                  {...stringAvatar(displayName, {
                                    fontSize: 13,
                                    color: "#fff !important",
                                  })}
                                />
                              )
                            }
                            sx={{
                              "& .MuiChip-avatar": {
                                width: 22,
                                height: 22,
                                fontSize: 10,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.light",
                  },
                  borderRadius: 2,
                }}
              >
                {(!children || children.length === 0) && (
                  <MenuItem
                    disabled
                    sx={{
                      py: 3,
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <ChildCare
                      sx={{ fontSize: 28, color: "action.disabled" }}
                    />
                    <Typography variant="body2" color="text.disabled">
                      {t("timeline.no_children")}
                    </Typography>
                  </MenuItem>
                )}
                {children?.map((child) => {
                  const childDisplayName = `${child.firstName} ${child.lastName}`;
                  const isSelected =
                    childrenSelected.indexOf(childDisplayName) > -1;
                  return (
                    <MenuItem
                      key={childDisplayName}
                      value={childDisplayName}
                      sx={childPickerMenuItemSx(isSelected)}
                    >
                      <ChildPickerOption
                        child={child}
                        isSelected={isSelected}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Badge
            badgeContent={activeFilterCount}
            color="error"
            invisible={activeFilterCount === 0}
          >
            <Chip
              icon={
                openFilter ? (
                  <Close sx={{ fontSize: 16 }} />
                ) : (
                  <FilterList sx={{ fontSize: 16 }} />
                )
              }
              label={
                openFilter
                  ? t("buttons.cancel")
                  : activeFilterCount > 0
                    ? t("timeline.filters_active")
                    : t("filter")
              }
              variant={openFilter ? "filled" : "outlined"}
              color={openFilter ? "primary" : "default"}
              onClick={() => setOpenFilter(!openFilter)}
              sx={{
                fontWeight: 500,
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            />
          </Badge>
        </Box>

        {openFilter && (
          <Box
            width="100%"
            sx={{
              pb: 2.5,
              pt: 0.5,
            }}
          >
            {/* Event type chips */}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                mb: 1,
                display: "block",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t("posts.types")}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2.5}>
              {postItems}
            </Box>

            {/* Date range */}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                mb: 1,
                display: "block",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t("labels.date")}
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18n.language}
            >
              <Box display="flex" gap={1.5} alignItems="center">
                <DesktopDatePicker
                  label={t("from")}
                  value={from}
                  onChange={(e) => setFrom(e)}
                  inputFormat="DD MMM YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{
                        maxWidth: { xs: "100%", sm: 200 },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "background.default",
                        },
                      }}
                    />
                  )}
                />
                <Box
                  sx={{
                    width: 24,
                    height: 2,
                    bgcolor: "text.disabled",
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                />
                <DesktopDatePicker
                  label={t("to")}
                  value={to}
                  onChange={(e) => setTo(e)}
                  inputFormat="DD MMM YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{
                        maxWidth: { xs: "100%", sm: 200 },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "background.default",
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        )}
      </Toolbar>

      {/* Main content */}
      <Container sx={{ mt: 3, pb: 10 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : filteredEvents.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={10}
            px={3}
            mx="auto"
            maxWidth={400}
            sx={{
              bgcolor: "background.paper",
              borderRadius: 3,
              mt: 4,
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            }}
          >
            <EventNote
              sx={{ fontSize: 56, mb: 1.5, color: "action.disabled" }}
            />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t("timeline.no_events")}
            </Typography>
            <Typography
              variant="body2"
              color="text.disabled"
              textAlign="center"
            >
              {t("timeline.no_events_hint")}
            </Typography>
          </Box>
        ) : (
          filteredEvents.map((event) => (
            <Event
              item={event}
              key={event.id}
              onCommentClick={openCommentDialog}
              onFrameIndicatorClick={openFrameIndicatorDialog}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </Container>

      {/* FAB / SpeedDial */}
      {authInfo.role === "PARENT" && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            pl: 2.5,
            pr: 3,
            gap: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: "0 4px 14px 0 rgba(0,0,0,0.25)",
            "&:hover": { boxShadow: "0 6px 20px 0 rgba(0,0,0,0.3)" },
          }}
          onClick={() => handleAddEvent("Observation")}
        >
          <Add />
          {t("posts.add.observation")}
        </Fab>
      )}

      {isStaff && (
        <SpeedDial
          ariaLabel={t("timeline.add_event")}
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
          }}
          icon={<SpeedDialIcon />}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={t(action.labelKey)}
              onClick={() => handleAddEvent(action.name)}
            />
          ))}
        </SpeedDial>
      )}

      {/* Dialogs */}
      {openDialog && (
        <EventDialog
          open={openDialog}
          child={children}
          onClose={() => {
            setOpenDialog(false);
            setEditEvent(null);
          }}
          event={eventType}
          editItem={editEvent}
        />
      )}
      {!!commentDialog && (
        <CommentDialog
          open={!!commentDialog}
          onClose={() => setCommentDialog("")}
          event={{ id: commentDialog }}
        />
      )}
      {!!frameIndicatorDialog && (
        <FrameworkIndicatorDialog
          open={!!frameIndicatorDialog}
          onClose={() => setFrameIndicatorDialog("")}
          event={{ id: frameIndicatorDialog }}
        />
      )}
      <Dialog
        open={!!deleteEvent}
        onClose={() => setDeleteEvent(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            color: "common.white",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <WarningAmber sx={{ fontSize: 24 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {t("buttons.delete")}
            </Typography>
            {deleteEvent?.eventType?.name && (
              <Chip
                label={t(`posts.${deleteEvent.eventType.name.toLowerCase()}`)}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  color: "common.white",
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            )}
          </Box>
          <MuiIconButton
            onClick={() => setDeleteEvent(null)}
            size="small"
            sx={{ color: "common.white" }}
          >
            <Close />
          </MuiIconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 3, textAlign: "center" }}>
          <Typography variant="body1">{t("alert.confirm_delete")}</Typography>
          {deleteEvent?.child && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              <strong>
                {deleteEvent.child.firstName} {deleteEvent.child.lastName}
              </strong>
            </Typography>
          )}
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={() => setDeleteEvent(null)}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            fullWidth
            disabled={deleting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {deleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.delete")
            )}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default Timeline;
